import { isEmpty, notEmpty, parseString } from "@de-xima/xima-common-js-lang";

import type { IMonacoJsonExtraSchema, IMonacoJsonSchema } from "../Header.ts";

const JsonSchemaRegistry = [] as IMonacoJsonExtraSchema[];

/**
 * @param currentSchemas List of already available schema.
 * @param additionalSchemas Additional schema to add.
 * @returns The current schemas with the additional schemas added, minus the duplicates.
 */
export function concatJsonSchemas(
  currentSchemas: IMonacoJsonSchema[],
  ...additionalSchemas: IMonacoJsonSchema[]
): IMonacoJsonSchema[] {
  const existingSchemasUris = new Set(currentSchemas.map((schema) => schema.uri));
  const result = [...currentSchemas];
  for (const schema of additionalSchemas) {
    if (schema !== undefined && !existingSchemasUris.has(schema.uri)) {
      result.push(schema);
    }
  }
  return result;
}

/**
 * @returns The list of JSON schemas that are currently registered.
 */
export function getRegisteredSchemas(): IMonacoJsonExtraSchema[] {
  return JsonSchemaRegistry;
}

/**
 * Adds a new JSON schema. These schemas are always loaded when creating a monaco editor for JSON.
 * You can then use a schema for a JSON file as follows:
 *
 * ```xml
 * <blut:monacoEditor id="edJson"
 *   value="#{resourceContentBean.resourceDataString}"
 *   extender="Formcycle.MonacoEditor.createExtender({jsonSchema: 'http://json-schema.org/learn/examples/geographical-location.schema.json'})"
 *   editorOptions="#{EMonacoOptions.JSON.value}"
 *   uiLanguage="#{localeBean.localeCode}"
 *   width="100%"
 *   height="100%"
 * />
 * ```
 *
 * In case the JSON schema URI is an HTTP URL and is accessible (CORS header set appropriately), it is fetched over
 * the network, and you do not even have to add the schemas via this method.
 * @param schema An object with the JSON schema.
 * @param uri URI of the schema, this is the identifier for the schema. If not
 * given, the schema must have an `$id` entry.
 */
export function registerJsonSchema(schema?: Record<string, unknown> | undefined, uri?: string): void {
  // Add only if no such schema exists yet
  const resolvedUri = isEmpty(uri) && schema !== undefined ? parseString(schema.$id) : uri;
  if (notEmpty(resolvedUri)) {
    if (!JsonSchemaRegistry.some((s) => s.uri === resolvedUri)) {
      JsonSchemaRegistry.push({ schema, uri: resolvedUri });
    }
  } else {
    console.error("URI must be given explicitly or via the $id property of the schema.");
    return;
  }
}

/**
 * Adds new JSON schemas. These schemas are always loaded for any monaco editor.
 * @param schemas Schema to register.
 * @see registerJsonSchema
 */
export function registerJsonSchemas(schemas: IMonacoJsonExtraSchema[]): void {
  for (const schema of schemas) {
    registerJsonSchema(schema.schema, schema.uri);
  }
}
